import { useState } from 'react'
import { AuthProvider, useAuth } from './modules/accounts/index.js'
import { useMe } from './modules/accounts/index.js'
import { useWorkerNotifications, type MessageActions, type WorkerNotification } from './modules/messages/index.js'
import { MessageSquareReply } from 'lucide-react'
import { Button, TIER_HEX, TIER_LABELS, Skeleton } from './modules/core/index.js'
import { employmentTypeNote } from './modules/core/index.js'
import type { MeWorker } from './modules/accounts/index.js'
import { BrandLogo } from './modules/core/index.js'
import { UserMenu } from './modules/accounts/index.js'
import { Login } from './modules/accounts/index.js'
import { AcceptInvitation, ForgotPassword, ResetPassword } from './modules/accounts/index.js'
import { WorkerHome, useWorkerStanding } from './modules/scoring/index.js'
import { WorkerMessages } from './modules/messages/index.js'
import { BridgeMessageActions, WorkerShifts } from './modules/schedule/index.js'
import { useFeatures } from './modules/core/index.js'
import { FeedbackButton } from './modules/feedback/index.js'
import { workerFeedbackApi } from './modules/feedback/index.js'
import { usePathname } from './modules/core/index.js'
import { WorkerIcon } from './modules/core/index.js'
import { DemoSessionBanner } from './modules/accounts/index.js'
import { WORKER_ROLE_LABELS } from './modules/core/index.js'
import { LoadFailure } from './modules/core/index.js'
import { HelpSeatProvider } from './modules/core/index.js'

/** A core tab, or an add-on's (`feature:<key>`, from the registry's `workerAppTab`). */
export type WorkerTab = 'home' | 'shifts' | 'messages' | `feature:${string}`

const TABS: ReadonlyArray<{ id: WorkerTab; label: string }> = [
  { id: 'home', label: 'Home' },
  { id: 'shifts', label: 'Shifts' },
  { id: 'messages', label: 'Messages' },
]

function TabButton({ active, label, badge = 0, onClick }: { active: boolean; label: string; badge?: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} aria-current={active ? 'page' : undefined} className={`flex-1 px-2 text-[11px] font-bold py-3 whitespace-nowrap ${active ? 'text-marine border-b-2 border-marine' : 'text-ink-mid'}`}>
      {label}
      {badge > 0 && (
        <span className="ml-1.5 inline-flex min-w-[1.25rem] justify-center rounded-full bg-coral px-1.5 text-[9px] font-extrabold text-white" aria-label={`${badge} unread`}>
          {badge}
        </span>
      )}
    </button>
  )
}

/**
 * The worker's own shell: who they are from `GET /auth/me`, then every
 * screen reads the ordinary `/workers/{workerId}` endpoints with that id.
 * Mobile first and one column: a worker opens this on a phone between
 * shifts.
 */
function WorkerShell() {
  const { isAuthenticated, role, username, logout } = useAuth()
  const pathname = usePathname()
  const [tab, setTab] = useState<WorkerTab>('home')
  // The Home banner opens Shifts straight onto the preferences wizard; each press is a new request.
  const [preferencesNonce, setPreferencesNonce] = useState(0)

  if (pathname === '/reset-password') {
    return <ResetPassword />
  }
  if (pathname === '/forgot-password') {
    return <ForgotPassword />
  }
  if (pathname === '/accept-invitation') {
    return <AcceptInvitation />
  }

  if (!isAuthenticated || !role) {
    return <Login title="Worker App" />
  }

  // An operator or facility administrator who signed in here belongs in the operator shell, which main.tsx picks on the next load.
  if (role !== 'worker') {
    window.location.replace('/')
    return null
  }

  return (
    <SignedInWorker
      username={username ?? 'Account'}
      tab={tab}
      onTab={setTab}
      onSignOut={logout}
      preferencesNonce={preferencesNonce}
      onOpenPreferences={() => {
        setPreferencesNonce((current) => current + 1)
        setTab('shifts')
      }}
    />
  )
}

function SignedInWorker({ username, tab, onTab, onSignOut, preferencesNonce, onOpenPreferences }: { username: string; tab: WorkerTab; onTab: (tab: WorkerTab) => void; onSignOut: () => void; preferencesNonce: number; onOpenPreferences: () => void }) {
  const meQuery = useMe()
  const me = meQuery.data
  const worker: MeWorker | null = me?.worker ?? null
  const fullName = me?.firstName ? [me.firstName, me.lastName].filter(Boolean).join(' ') : null
  // The badge's count: the same live query the Messages screen reads, so the two never disagree.
  const notificationsQuery = useWorkerNotifications(worker?.workerId ?? null, { live: true })
  const unreadCount = notificationsQuery.data?.filter((notification) => !notification.read).length ?? 0
  // The tier under the header (the demo's "Bronze Tier" line): the standing Home reads anyway, so one shared query.
  const standing = useWorkerStanding(worker?.workerId ?? null)
  const tier = standing.data?.tier ?? null
  const features = useFeatures()
  /**
   * What a message card can do beyond being read, by kind: a Bridge alert
   * is answered on the card, a confirmation leads to Shifts, a facility's
   * reply leads to the note on Home. Composed here because the buttons
   * belong to other modules than the card.
   */
  const renderMessageActions: MessageActions = (notification: WorkerNotification) => {
    if (!worker) {
      return null
    }
    if (notification.type === 'bridge_callout' || notification.type === 'bridge_filled') {
      return <BridgeMessageActions workerId={worker.workerId} message={notification} onOpenShifts={() => onTab('shifts')} />
    }
    if (notification.type === 'barrier_reply') {
      return (
        <Button icon={MessageSquareReply} variant="secondary" size="sm" onClick={() => onTab('home')}>
          Read the reply
        </Button>
      )
    }
    return null
  }
  // Each add-on's tab, after the core ones, only while it is on at the worker's facility.
  const featureTabs = features.flatMap((feature) => (feature.workerAppTab && worker?.features?.[feature.key] === true ? [{ id: `feature:${feature.key}` as const, label: feature.workerAppTab.label, Page: feature.workerAppTab.page }] : []))
  const featureTab = featureTabs.find((entry) => entry.id === tab) ?? null
  // An add-on switched off while its tab was open leaves nothing to show: back to Home.
  const shownTab: WorkerTab = tab.startsWith('feature:') && !featureTab ? 'home' : tab

  return (
    <div className="min-h-screen bg-canvas">
      <header className="bg-marine-dk px-5 py-4">
        <div className="max-w-md mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            {/* Above the name, not beside it: at a phone's width there is no room for both on one line. */}
            <div className="mb-1.5">
              <BrandLogo className="h-8" />
            </div>
            <div className="text-base font-black text-white truncate">{fullName ?? 'Worker App'}</div>
            {worker && (
              <div className="text-[11px] text-white/70 truncate flex items-center gap-1.5">
                <WorkerIcon className="w-3.5 h-3.5 text-white/70" />
                <span className="truncate">
                  {worker.facilityName ?? worker.facilityId} · {WORKER_ROLE_LABELS[worker.role] ?? worker.role}
                </span>
              </div>
            )}
            {tier && (
              <div className="mt-1 flex items-center gap-1.5 text-[11px] font-bold" style={{ color: TIER_HEX[tier] }}>
                <span aria-hidden="true" className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: TIER_HEX[tier] }} />
                {TIER_LABELS[tier]} Tier
                {standing.data && employmentTypeNote(standing.data) && <span className="ml-1 rounded bg-white/15 px-1.5 py-0.5 text-[10px] font-bold text-white/90">{employmentTypeNote(standing.data)}</span>}
              </div>
            )}
          </div>
          <UserMenu username={username} firstName={me?.firstName} lastName={me?.lastName} organizationName={me?.organizationName} onSignOut={onSignOut} />
        </div>
      </header>
      <DemoSessionBanner />

      <nav className="max-w-md mx-auto flex overflow-x-auto border-b border-marine/15 bg-white">
        {TABS.map((entry) => (
          <TabButton key={entry.id} active={shownTab === entry.id} label={entry.label} badge={entry.id === 'messages' ? unreadCount : 0} onClick={() => onTab(entry.id)} />
        ))}
        {featureTabs.map((entry) => (
          <TabButton key={entry.id} active={shownTab === entry.id} label={entry.label} onClick={() => onTab(entry.id)} />
        ))}
      </nav>

      {meQuery.isPending ? (
        <Skeleton lines={5} className="p-8" />
      ) : meQuery.isError ? (
        <LoadFailure className="p-8" message="Your sign-in could not be checked." onRetry={() => void meQuery.refetch()} retrying={meQuery.isFetching} />
      ) : !worker ? (
        <div className="max-w-md mx-auto p-6 text-sm text-ink-mid">
          <p>
            <span className="font-bold text-ink">Your roster record could not be found.</span> Your login is still here, but the facility no longer lists you. Ask your scheduler.
          </p>
        </div>
      ) : shownTab === 'home' ? (
        <WorkerHome workerId={worker.workerId} onOpenShifts={onOpenPreferences} />
      ) : shownTab === 'shifts' ? (
        <WorkerShifts workerId={worker.workerId} facilityId={worker.facilityId} openPreferencesNonce={preferencesNonce} />
      ) : featureTab ? (
        <featureTab.Page workerId={worker.workerId} />
      ) : (
        <WorkerMessages workerId={worker.workerId} renderActions={renderMessageActions} />
      )}

      <FeedbackButton
        api={workerFeedbackApi}
        details={{
          role: 'worker',
          organization: me?.organizationName,
          facilityId: worker?.facilityId,
          facility: worker?.facilityName ?? undefined,
          view: TABS.find((entry) => entry.id === tab)?.label ?? tab,
        }}
      />
    </div>
  )
}

export default function WorkerApp() {
  return (
    <AuthProvider>
      <HelpSeatProvider seat="worker">
        <WorkerShell />
      </HelpSeatProvider>
    </AuthProvider>
  )
}
